import { useCallback, useRef, useState } from 'react'
import { mutations } from '../../../../../../api'
import { useAppSelector } from '../../../../../../redux/hooks'
import { SvgIcon } from '../../../../../../shared/icons'
import { Message } from '../../../../../../types/api'
import EmojiWrapper from '../../message-input/EmojiWrapper'

const DEFAULT_EMOJIS = ['👍', '❤️', '😂', '😮', '😢']

const MessageActions = ({ message }: { message: Message }) => {
  const { user } = useAppSelector((store) => store.auth)
  const { mutate: toggleReaction } = mutations.useToogleReaction()
  const [showAllEmojis, setShowAllEmojis] = useState(false)
  const [emojiPickerDisabled, setEmojiPickerDisabled] = useState(true)
  const touchTriggeredRef = useRef(false)

  const handleQuickReaction = (emoji: string) => {
    toggleReaction({ messageId: message.id, emoji: { emoji } })
  }

  const handleEmojiPickerSelect = (emoji: { emoji: string }) => {
    const emojiString = emoji.emoji
    toggleReaction({ messageId: message.id, emoji: { emoji: emojiString } })
  }

  // On mobile, touchstart fires before mouseenter/click.
  // We use this to detect that the interaction came from a touch device
  // so we can prevent the emoji picker from opening on the first tap.
  const handleTouchStart = useCallback(() => {
    touchTriggeredRef.current = true
  }, [])

  const handleMouseEnter = useCallback(() => {
    setShowAllEmojis(true)
    // On desktop (no touch), enable the picker immediately on hover
    if (!touchTriggeredRef.current) {
      setEmojiPickerDisabled(false)
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    setShowAllEmojis(false)
    setEmojiPickerDisabled(true)
    touchTriggeredRef.current = false
  }, [])

  // On mobile, enable the picker only after the tray is already visible
  // and the user explicitly taps the "+" button a second time.
  const handlePlusClick = useCallback(() => {
    if (touchTriggeredRef.current && emojiPickerDisabled) {
      // First tap on mobile: show the tray and enable picker for next tap
      setShowAllEmojis(true)
      setEmojiPickerDisabled(false)
    }
  }, [emojiPickerDisabled])

  return (
    <div
      className="quick-reactions-container"
      onTouchStart={handleTouchStart}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div onClick={handlePlusClick}>
        <EmojiWrapper
          position={message.sender_id === user?.id ? 'left' : 'right'}
          id={`emoji-reaction-${message.id}`}
          onEmojiSelect={handleEmojiPickerSelect}
          className="popover-trigger"
          disabled={emojiPickerDisabled}
        >
          <button className="quick-reaction-btn plus-btn" type="button">
            <SvgIcon
              className={showAllEmojis ? 'plus-icon' : 'smile-icon'}
              iconId={showAllEmojis ? 'plus' : 'smile-2'}
            />
          </button>
        </EmojiWrapper>
      </div>
      <div className={`emoji-slider ${showAllEmojis ? 'show' : ''}`}>
        {DEFAULT_EMOJIS.map((emoji, index) => (
          <button
            key={index}
            className="quick-reaction-btn"
            onClick={() => handleQuickReaction(emoji)}
            type="button"
            style={{ transitionDelay: `${index * 30}ms` }}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  )
}

export default MessageActions
